import { Fragment, type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { getAbsoluteUrl } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { aroundItemHomeV2QueueItemsEdge$key } from "scmp-app/queries/__generated__/aroundItemHomeV2QueueItemsEdge.graphql";

import {
  Container,
  Content,
  ItemDivider,
  StyledContentItemHomeAroundMorningStudioPrimaryV2,
  StyledContentItemHomeAroundMorningStudioSecondaryV2,
  StyledContentItemHomeAroundPrimaryV2,
  StyledContentItemHomeAroundSecondaryV2,
  StyledScmpLabLogo,
  Title,
  TitleContainer,
} from "./styles";

const handleClick = (entityId = "", urlAlias = "") => {
  sendGA4Tracking({
    action: "click",
    category: "recirculation",
    customized_parameters: {
      article_id: entityId,
      destination_url: getAbsoluteUrl(urlAlias),
      page_type: "homepage",
      widget_name: "around_scmp",
    },
    subcategory: "widget",
  });
};

type Props = {
  className?: string;
  reference: aroundItemHomeV2QueueItemsEdge$key;
  title: string;
  urlAlias: string;
  withSCMPLabLogo?: boolean;
};

export const HomeV2AroundItem: FunctionComponent<Props> = ({
  className,
  reference,
  title,
  urlAlias,
  withSCMPLabLogo,
}) => {
  const data = useFragment(
    graphql`
      fragment aroundItemHomeV2QueueItemsEdge on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Content {
            entityId
            ...homeAroundPrimaryV2ContentItemContent
            ...homeAroundSecondaryV2ContentItemContent
            ...homeAroundMorningStudioPrimaryV2ContentItemContent
            ...homeAroundMorningStudioSecondaryV2ContentItemContent
          }
        }
      }
    `,
    reference,
  );

  const isMorningStudio = title === "MORNING STUDIO";

  const renderItem = (item: (typeof data)[number], index: number) => {
    if (isMorningStudio) {
      return index === 0 ? (
        <StyledContentItemHomeAroundMorningStudioPrimaryV2
          onClick={handleClick}
          reference={item.node}
        />
      ) : (
        <StyledContentItemHomeAroundMorningStudioSecondaryV2
          onClick={handleClick}
          reference={item.node}
        />
      );
    }
    return index === 0 ? (
      <StyledContentItemHomeAroundPrimaryV2 onClick={handleClick} reference={item.node} />
    ) : (
      <StyledContentItemHomeAroundSecondaryV2 onClick={handleClick} reference={item.node} />
    );
  };

  return (
    <Container className={className}>
      {withSCMPLabLogo ? (
        <TitleContainer>
          <Title pathname={urlAlias}>{title}</Title>
          <StyledScmpLabLogo />
        </TitleContainer>
      ) : (
        <Title pathname={urlAlias}>{title}</Title>
      )}
      <Content>
        {data?.map((item, index) => (
          <Fragment key={item.node.entityId}>
            {index > 0 && <ItemDivider />}
            {renderItem(item, index)}
          </Fragment>
        ))}
      </Content>
    </Container>
  );
};

HomeV2AroundItem.displayName = "HomeV2AroundItem";
